import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@/store";
import { UserSummary, UpdateUserRequest, AddUserRequest, OrgRole } from "../dtos/User";
import { userService } from "../infrastructure/user-service";

interface UserState {
  organizationUsers: UserSummary[];
  organizationRoles: OrgRole[];
  isLoading: boolean;
  rolesLoading: boolean;
  error: string | null;
}

const initialState: UserState = {
  organizationUsers: [],
  organizationRoles: [],
  isLoading: false,
  rolesLoading: false,
  error: null,
};

export const fetchOrganizationUsers = createAsyncThunk<
  UserSummary[],
  string,
  { rejectValue: string; state: RootState }
>("users/fetchOrganizationUsers", async (organizationId, { rejectWithValue }) => {
  try {
    const response = await userService.getOrganizationUsers(organizationId);
    return response.users;
  } catch (err: unknown) {
    const message = (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? "Failed to load organization users";
    return rejectWithValue(message);
  }
});

export const addOrganizationUserThunk = createAsyncThunk<
  { user: UserSummary },
  { organizationId: string; data: AddUserRequest },
  { rejectValue: string; state: RootState }
>("users/addOrganizationUser", async ({ organizationId, data }, { rejectWithValue }) => {
  try {
    const response = await userService.addOrganizationUser(organizationId, data);
    return { user: response.user };
  } catch (err: unknown) {
    const message = (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? "Failed to add user";
    return rejectWithValue(message);
  }
});

export const updateOrganizationUserThunk = createAsyncThunk<
  { userId: string; firstName: string; lastName: string; phoneNumber?: string | null; roleId?: string | null },
  { organizationId: string; userId: string; data: UpdateUserRequest },
  { rejectValue: string; state: RootState }
>("users/updateOrganizationUser", async ({ organizationId, userId, data }, { rejectWithValue }) => {
  try {
    await userService.updateOrganizationUser(organizationId, userId, data);
    return { userId, ...data };
  } catch (err: unknown) {
    const message = (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? "Failed to update user";
    return rejectWithValue(message);
  }
});

export const removeOrganizationUserThunk = createAsyncThunk<
  string,
  { organizationId: string; userId: string },
  { rejectValue: string; state: RootState }
>("users/removeOrganizationUser", async ({ organizationId, userId }, { rejectWithValue }) => {
  try {
    await userService.removeOrganizationUser(organizationId, userId);
    return userId;
  } catch (err: unknown) {
    const message = (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? "Failed to remove user";
    return rejectWithValue(message);
  }
});

export const fetchOrganizationRoles = createAsyncThunk<
  OrgRole[],
  string,
  { rejectValue: string; state: RootState }
>("users/fetchOrganizationRoles", async (organizationId, { rejectWithValue }) => {
  try {
    const response = await userService.getOrganizationRoles(organizationId);
    return response.roles;
  } catch (err: unknown) {
    const message = (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? "Failed to load roles";
    return rejectWithValue(message);
  }
});

export const assignOrganizationUserRoleThunk = createAsyncThunk<
  { userId: string; roleId: string; roleName: string },
  { organizationId: string; userId: string; roleId: string; roleName: string },
  { rejectValue: string; state: RootState }
>("users/assignOrganizationUserRole", async ({ organizationId, userId, roleId, roleName }, { rejectWithValue }) => {
  try {
    await userService.assignUserRole(organizationId, userId, { roleId });
    return { userId, roleId, roleName };
  } catch (err: unknown) {
    const message = (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? "Failed to assign role";
    return rejectWithValue(message);
  }
});

const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    clearUsersState(state) {
      state.organizationUsers = [];
      state.organizationRoles = [];
      state.isLoading = false;
      state.rolesLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrganizationUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organizationUsers = action.payload;
      })
      .addCase(fetchOrganizationUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? "Failed to load organization users";
      })
      .addCase(addOrganizationUserThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addOrganizationUserThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organizationUsers.push(action.payload.user);
      })
      .addCase(addOrganizationUserThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? "Failed to add user";
      })
      .addCase(updateOrganizationUserThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOrganizationUserThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedRole = action.payload.roleId
          ? state.organizationRoles.find((r) => r.id === action.payload.roleId)
          : null;

        state.organizationUsers = state.organizationUsers.map((user) =>
          user.id === action.payload.userId
            ? {
              ...user,
              firstName: action.payload.firstName,
              lastName: action.payload.lastName,
              phoneNumber: action.payload.phoneNumber,
              roleId: action.payload.roleId !== undefined ? action.payload.roleId : user.roleId,
              roleName: action.payload.roleId !== undefined
                ? (updatedRole ? updatedRole.name : user.roleName)
                : user.roleName
            }
            : user
        );
      })
      .addCase(updateOrganizationUserThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? "Failed to update user";
      })
      .addCase(removeOrganizationUserThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeOrganizationUserThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organizationUsers = state.organizationUsers.filter(
          (user) => user.id !== action.payload
        );
      })
      .addCase(removeOrganizationUserThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? "Failed to remove user";
      })
      .addCase(fetchOrganizationRoles.pending, (state) => {
        state.rolesLoading = true;
      })
      .addCase(fetchOrganizationRoles.fulfilled, (state, action) => {
        state.rolesLoading = false;
        state.organizationRoles = action.payload;
      })
      .addCase(fetchOrganizationRoles.rejected, (state) => {
        state.rolesLoading = false;
      })
      .addCase(assignOrganizationUserRoleThunk.fulfilled, (state, action) => {
        state.organizationUsers = state.organizationUsers.map((user) =>
          user.id === action.payload.userId
            ? { ...user, roleId: action.payload.roleId, roleName: action.payload.roleName }
            : user
        );
      });
  },
});

export const { clearUsersState } = userSlice.actions;
export default userSlice.reducer;

export const selectUsersState = (state: RootState) => state.users;
export const selectOrganizationUsers = (state: RootState) => state.users.organizationUsers;
export const selectOrganizationRoles = (state: RootState) => state.users.organizationRoles;
export const selectUsersLoading = (state: RootState) => state.users.isLoading;
export const selectRolesLoading = (state: RootState) => state.users.rolesLoading;
export const selectUsersError = (state: RootState) => state.users.error;
